import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { api } from '../services/api';
import { parseCalendarDate, formatCalendarDate, formatTimeOfDay } from '../utils/dateTime';

const formatParcelDayDate = (dateString: string): string => formatCalendarDate(dateString);

// Helper to get day of week index (0 = Sunday, 6 = Saturday)
const getDayOfWeekIndex = (dayName: string): number => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days.indexOf(dayName.toLowerCase());
};

// Format order time with actual dates
const formatOrderTimeWithDates = (parcelDayDate: string, startDay: string, startTime: string, endDay: string, endTime: string): string => {
  // Parse the parcel day date explicitly
  const parcelDate = parseCalendarDate(parcelDayDate);  // ✅ Use explicit parsing
  const parcelDayOfWeek = parcelDate.getDay(); // 0 = Sunday, 6 = Saturday
  
  const startDayIndex = getDayOfWeekIndex(startDay);
  const endDayIndex = getDayOfWeekIndex(endDay);
  
  // Calculate the date for start day (working backwards from parcel day)
  const startDate = new Date(parcelDate);
  let daysToSubtract = parcelDayOfWeek - startDayIndex;
  if (daysToSubtract < 0) {
    daysToSubtract += 7;
  }
  if (daysToSubtract === 0) {
    // If it's the same day of week, check if we need to go back a week
    // For order times, we typically want the most recent occurrence before/on parcel day
    daysToSubtract = 7;
  }
  startDate.setDate(startDate.getDate() - daysToSubtract);
  
  // Calculate the date for end day (working backwards from parcel day)
  const endDate = new Date(parcelDate);
  let endDaysToSubtract = parcelDayOfWeek - endDayIndex;
  if (endDaysToSubtract < 0) {
    endDaysToSubtract += 7;
  }
  if (endDaysToSubtract === 0 && daysToSubtract !== 0) {
    // If end day is same as parcel day, use parcel day
    endDaysToSubtract = 0;
  }
  endDate.setDate(endDate.getDate() - endDaysToSubtract);
  
  // Format times (TIME from API)
  const formatTime = (time: string) => formatTimeOfDay(time);

  // Format dates
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    });
  };
  
  const startTimeFormatted = formatTime(startTime);
  const endTimeFormatted = formatTime(endTime);
  const startDateFormatted = formatDate(startDate);
  const endDateFormatted = formatDate(endDate);
  
  return `Place orders between ${startTimeFormatted} ${startDateFormatted} and ${endTimeFormatted} ${endDateFormatted}`;
};

interface OrderTime {
  id: number;
  name: string;
  start_day: string;
  start_time: string;
  end_day: string;
  end_time: string;
}

interface PickupSchedule {
  id: number;
  name: string;
  max_capacity: number;
}

interface ParcelDayInfo {
  id: number;
  date: string;
  weekday: string;
  is_currently_open: boolean;
  status: 'open' | 'upcoming' | 'future';
  status_text: string;
  color_code: 'green' | 'orange' | 'red' | 'blue' | 'grey';
  order_time: OrderTime | null;
  pickup_schedule: PickupSchedule | null;
  notes: string | null;
}

interface ParcelDayData {
  current_week: ParcelDayInfo[];
  next_week: ParcelDayInfo[];
  next_available_day: {
    id: number;
    date: string;
    weekday: string;
  } | null;
  can_place_orders: boolean;
}

interface ParcelDayInfoProps {
  onParcelDaySelected?: (parcelDay: ParcelDayInfo) => void;
  /** When provided (e.g. from Food Parcels page), avoids a second API call. */
  initialData?: ParcelDayData | null;
}

const ParcelDayInfo: React.FC<ParcelDayInfoProps> = ({ onParcelDaySelected, initialData }) => {
  const [data, setData] = useState<ParcelDayData | null>(initialData ?? null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData != null) {
      setData(initialData);
      setLoading(false);
      return;
    }
    const fetchParcelDays = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/v1/parcel-days/available-for-order');
        setData(response.data.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchParcelDays();
  }, [initialData]);

  // Function to scroll to food parcel selection section
  const scrollToFoodParcels = () => {
    // Find the "Select Your Food Parcel" section by looking for the heading
    const foodParcelSection = Array.from(document.querySelectorAll('h2')).find(
      (heading) => heading.textContent?.includes('Select Your Food Parcel')
    );
    
    if (foodParcelSection) {
      // Scroll to the section with smooth animation
      foodParcelSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  // Remove unused functions completely

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No parcel day information available.
      </Alert>
    );
  }







  return (
    <Box>

      {/* Current Week Parcel Days - Enhanced Prominent Layout */}
      {data.current_week.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            This Week's Parcel Days:
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {data.current_week.map((parcelDay) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={parcelDay.id}>
                <Card
                  key={parcelDay.id}
                  sx={{
                    mb: 2,
                    border: parcelDay.is_currently_open ? '4px solid #4caf50' : '2px solid #e0e0e0',
                    backgroundColor: parcelDay.is_currently_open ? '#f8fff8' : 'white',
                    cursor: parcelDay.is_currently_open ? 'pointer' : 'default',
                    '&:hover': parcelDay.is_currently_open ? {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    } : {},
                    transition: 'all 0.2s ease-in-out',
                    minHeight: 160,
                    boxShadow: parcelDay.is_currently_open ? 3 : 1,
                  }}
                  onClick={() => {
                    if (parcelDay.is_currently_open) {
                      // Scroll to food parcel selection section
                      scrollToFoodParcels();
                      // Also call the onParcelDaySelected callback if provided
                      if (onParcelDaySelected) {
                        onParcelDaySelected(parcelDay);
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Status chip at top */}
                      <Box display="flex" justifyContent="center">
                        <Chip
                          label={parcelDay.is_currently_open ? 'OPEN NOW' : 'CLOSED'}
                          color={parcelDay.is_currently_open ? 'success' : 'error'}
                          sx={{ 
                            fontSize: '1rem',
                            height: 36,
                            minWidth: 100,
                            fontWeight: 'bold',
                            '& .MuiChip-label': {
                              px: 2
                            }
                          }}
                        />
                      </Box>
                      {/* Date with icon */}
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                        <CalendarToday sx={{ fontSize: 32, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                          {formatParcelDayDate(parcelDay.date)}
                        </Typography>
                      </Box>
                      {/* Order time details */}
                      {parcelDay.order_time && (
                        <Typography 
                          variant="body1" 
                          color={parcelDay.is_currently_open ? 'success.main' : 'text.secondary'}
                          sx={{ 
                            fontWeight: parcelDay.is_currently_open ? 'bold' : 'medium',
                            fontSize: '1.1rem',
                            lineHeight: 1.4,
                            textAlign: 'center',
                          }}
                        >
                          {formatOrderTimeWithDates(
                            parcelDay.date,
                            parcelDay.order_time.start_day,
                            parcelDay.order_time.start_time,
                            parcelDay.order_time.end_day,
                            parcelDay.order_time.end_time
                          )}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Order Status Alert */}
      {!data.can_place_orders && (
        <Alert severity="warning" sx={{ mb: 3, fontSize: '1rem', py: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            <strong>🚫 Orders are currently closed for this week.</strong> Please check the schedule below for when ordering will be available.
          </Typography>
        </Alert>
      )}

      {/* Next Week Parcel Days */}
      {data.next_week.length > 0 && (
        <Box>
          <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Next Week's Parcel Days:
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {data.next_week.map((parcelDay) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={parcelDay.id}>
                <Card
                  key={parcelDay.id}
                  sx={{
                    mb: 2,
                    border: parcelDay.is_currently_open ? '4px solid #4caf50' : '2px solid #e0e0e0',
                    backgroundColor: parcelDay.is_currently_open ? '#f8fff8' : 'white',
                    cursor: parcelDay.is_currently_open ? 'pointer' : 'default',
                    '&:hover': parcelDay.is_currently_open ? {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    } : {},
                    transition: 'all 0.2s ease-in-out',
                    minHeight: 160,
                    boxShadow: parcelDay.is_currently_open ? 3 : 1,
                  }}
                  onClick={() => {
                    if (parcelDay.is_currently_open) {
                      // Scroll to food parcel selection section
                      scrollToFoodParcels();
                      // Also call the onParcelDaySelected callback if provided
                      if (onParcelDaySelected) {
                        onParcelDaySelected(parcelDay);
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Status chip at top */}
                      <Box display="flex" justifyContent="center">
                        <Chip
                          label={parcelDay.is_currently_open ? 'OPEN NOW' : 'CLOSED'}
                          color={parcelDay.is_currently_open ? 'success' : 'error'}
                          sx={{ 
                            fontSize: '1rem',
                            height: 36,
                            minWidth: 100,
                            fontWeight: 'bold',
                            '& .MuiChip-label': {
                              px: 2
                            }
                          }}
                        />
                      </Box>
                      {/* Date with icon */}
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                        <CalendarToday sx={{ fontSize: 32, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                          {formatParcelDayDate(parcelDay.date)}
                        </Typography>
                      </Box>
                      {/* Order time details */}
                      {parcelDay.order_time && (
                        <Typography 
                          variant="body1" 
                          color={parcelDay.is_currently_open ? 'success.main' : 'text.secondary'}
                          sx={{ 
                            fontWeight: parcelDay.is_currently_open ? 'bold' : 'medium',
                            fontSize: '1.1rem',
                            lineHeight: 1.4,
                            textAlign: 'center',
                          }}
                        >
                          {formatOrderTimeWithDates(
                            parcelDay.date,
                            parcelDay.order_time.start_day,
                            parcelDay.order_time.start_time,
                            parcelDay.order_time.end_day,
                            parcelDay.order_time.end_time
                          )}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* No Parcel Days Available */}
      {data.current_week.length === 0 && data.next_week.length === 0 && (
        <Alert severity="info" sx={{ fontSize: '1rem', py: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            No parcel days are currently configured. Please check back later or contact support.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ParcelDayInfo; 